import { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, X } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Convert a stored path/url to a fully qualified URL the browser can load.
// Stored values look like "/api/uploads/abc.png" → prepend BACKEND_URL.
// External urls (http/https) pass through.
export function resolveImageUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
}

/**
 * Combined image input:
 *  - Upload button (calls /api/uploads/image, admin-protected)
 *  - URL text field (fallback / paste external URL)
 *  - Live preview
 *
 * value  : string  (stored as URL or "/api/uploads/<file>")
 * onChange(newUrl)
 */
export default function ImageUploader({ value, onChange, label = "Image", testId = "image" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  async function handleFile(file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div data-testid={`uploader-${testId}`}>
      <div className="flex gap-3 items-start">
        <div className="w-20 h-20 border border-[#E5E0D8] bg-[#F4F1EA] flex items-center justify-center overflow-hidden shrink-0">
          {value ? (
            <img
              src={resolveImageUrl(value)}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.opacity = "0.2"; }}
            />
          ) : (
            <Upload className="w-5 h-5 text-[#8C7A6B]" />
          )}
        </div>

        <div className="flex-1 space-y-2">
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="rounded-none uppercase tracking-[0.2em] text-[11px]"
              data-testid={`uploader-btn-${testId}`}
            >
              <Upload className="w-3.5 h-3.5 mr-2" />
              {uploading ? "..." : "Upload"}
            </Button>
            {value && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => onChange("")}
                className="rounded-none uppercase tracking-[0.2em] text-[11px] text-[#8C7A6B]"
                data-testid={`uploader-clear-${testId}`}
              >
                <X className="w-3.5 h-3.5 mr-1" /> Clear
              </Button>
            )}
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid={`uploader-file-${testId}`}
            />
          </div>
          <Input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Or paste image URL…"
            className="bg-transparent border-0 border-b border-[#E5E0D8] rounded-none focus-visible:ring-0 focus-visible:border-[#1A1A1A] px-0 font-mono text-xs"
            data-testid={`uploader-url-${testId}`}
          />
        </div>
      </div>
    </div>
  );
}
